import React from "react";
import {
  Box,
  List,
  ListItem,
  Text,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spinner,
  useDisclosure,
  HStack,
  VStack,
  Button,
  Icon,
  Divider,
  Stack,
  Center,
} from "@chakra-ui/react";
import { useKGBatchContext } from "context/batch/KGBatchContext";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FaPlay, FaTrash, FaRunning } from "react-icons/fa";
import { AddIcon } from "@chakra-ui/icons";
import { MdEdit } from "react-icons/md";
import DeleteJobModal from "../modals/DeleteJobModal";
import RunJobModal from "../modals/RunJobModal";
import CreateJobModal from "../modals/CreateJobModal";
import RenameJobModal from "../modals/RenameJobModal";
import { Header } from "build-ts/metadata";
import { createOrUpdateJob, deleteJob, runJob, getJob } from "../../../services/BatchServices";
import { DateFilter } from "build-ts/relative_date";
import { DateFilterCategory } from "utils/enum";
import showToast from "hooks/useCustomToast";
import { sortBy } from "utils/sortUtils";
import { useKGAnalysisPlanContext } from "context/batch/KGAnalysisPlanContext";
import { IoDuplicate } from "react-icons/io5";

const JobsContainer: React.FC = () => {
  const {
    jobHeaders,
    jobLoaders,
    selectedJobId,
    selectedJob,
    setSelectedJob,
    setSelectedJobId,
    fetchJobDetails,
    setJobHeaders,
    setJobRunHeaders,
    jobRunHeaders,
    toggleStepsContainer,
    setShowStepsContainer,
    setSelectedStepIndex,
    setActionRequestsByStepId,
    setAnalysisActionRequests,
    setShowStatusPage,
    setSelectedRunHeader,
    duplicateJob,
  } = useKGBatchContext();

  const deleteModal = useDisclosure();
  const runModal = useDisclosure();
  const createModal = useDisclosure();
  const renameModal = useDisclosure();
  
  // State for run job modal
  const [selectedReport, setSelectedReport] = React.useState<Header | null>(null);
  const [dateFilter, setDateFilter] = React.useState<DateFilter>();
  const [dateFilterCategory, setDateFilterCategory] = React.useState<DateFilterCategory>(DateFilterCategory.ROLLING);
  
  // State for create/rename job modal
  const [newJobName, setNewJobName] = React.useState("");

  // Add KGAnalysisPlanContext
  const { setPlan, setNodeMap, setSelectedMetricHeader, duplicatePlan } = useKGAnalysisPlanContext();

  const handleJobClick = async (header: Header) => {
    if (header.id) {
      if (selectedJobId === header.id) {
        // If clicking the same job, toggle the steps container
        toggleStepsContainer();
      } else {
        // If clicking a different job, select it and show steps
        setSelectedJobId(header.id);
        await fetchJobDetails(header.id);
        
        // Reset the plan context when selecting a different job
        setPlan(null);
        setNodeMap(new Map());
        setSelectedMetricHeader([]);
        setSelectedStepIndex(null);
        setAnalysisActionRequests([]);
        setActionRequestsByStepId(new Map());
        setShowStepsContainer(true);
      }
    }
  };

  const handleMenuClick = async (header: Header, e: React.MouseEvent) => {
    e.stopPropagation();
    if (header.id && selectedJobId !== header.id) {
      setSelectedJobId(header.id);
      await fetchJobDetails(header.id);
    }
  };

  const handleActionClick = async (header: Header, action: "delete" | "run" | "rename" | "duplicate") => {
    if (header.id) {
      setSelectedJobId(header.id);
      switch (action) {
        case "delete":
          deleteModal.onOpen();
          break;
        case "run":
          runModal.onOpen();
          break;
        case "rename":
          setNewJobName(header.name || "");
          renameModal.onOpen();
          break;
        case "duplicate":
          await handleDuplicateJob(header);
          break;
      }
    }
  };

  const handleDeleteJob = async (jobId: string) => {
    const response = await deleteJob(jobId);
    if (response) {
      setJobHeaders(jobHeaders.filter(header => header.id !== jobId));
      setSelectedJobId(null);
      setSelectedJob(null);
      setSelectedStepIndex(null);
      setAnalysisActionRequests([]);
      setActionRequestsByStepId(new Map());
      setShowStepsContainer(false);
      setShowStatusPage(false);
      setSelectedRunHeader(null);
      setSelectedMetricHeader([]);
      setPlan(null);
      setNodeMap(new Map());
    }
  };

  const handleReportHeaderSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const report = jobHeaders.find(header => header.id === event.target.value);
    setSelectedReport(report || null);
  };

  const handleRenameJob = async () => {
    if (newJobName.trim()) {
      const updatedJobRes = await createOrUpdateJob({
        id: selectedJob?.header?.id,
        name: newJobName,
        request: selectedJob?.request,
      });
      if (updatedJobRes) {
        const updatedJobHeader: Header[] = [];
        jobHeaders.forEach(header => {
          if (header.id !== updatedJobRes.header?.id) {
            updatedJobHeader.push(header);
          }
        });
        updatedJobHeader.push(updatedJobRes?.header!);
        setJobHeaders(updatedJobHeader);
        setSelectedJobId(updatedJobRes?.header?.id!);
        setSelectedJob(updatedJobRes);
        renameModal.onClose();
      }
    }
  };

  const handleCreateJob = async () => {
    if (newJobName.trim()) {
      const response = await createOrUpdateJob({
        name: newJobName,
      });
      if (response) {
        setJobHeaders([...jobHeaders, response?.header!]);
        setSelectedJobId(response?.header?.id!);
        setSelectedJob(response);
        
        // Reset the plan context when creating a new job
        setPlan(null);
        setNodeMap(new Map());
        setSelectedMetricHeader([]);
        setShowStepsContainer(true);
        setSelectedStepIndex(null);
        setActionRequestsByStepId(new Map());
        setJobRunHeaders([]);
        setAnalysisActionRequests([]);
        createModal.onClose();
      }
    }
  };

  const handleDuplicateJob = async (header: Header) => {
    if (!header.id) {
      showToast("Invalid job header", "", "error");
      return;
    }

    // Fetch the job details if not already selected
    let jobToDuplicate = selectedJob;
    if (!jobToDuplicate || jobToDuplicate.header?.id !== header.id) {
      const fetchedJob = await getJob(header.id);
      if (!fetchedJob) {
        showToast("Failed to fetch job details", "", "error");
        return;
      }
      jobToDuplicate = fetchedJob;
    }

    // Call the duplicateJob function from context
    const duplicatedJobHeader = await duplicateJob(jobToDuplicate, duplicatePlan);
    
    if (duplicatedJobHeader) {
      // Select the newly duplicated job
      setSelectedJobId(duplicatedJobHeader.id!);
      await fetchJobDetails(duplicatedJobHeader.id!);
      
      // Reset the plan context when selecting the duplicated job
      setPlan(null);
      setNodeMap(new Map());
      setSelectedMetricHeader([]);
      setSelectedStepIndex(null);
      setAnalysisActionRequests([]);
      setActionRequestsByStepId(new Map());
      setShowStepsContainer(true);
    }
  };

  const updateJobRunHeaders = async () => {
    showToast("Job run has started", "", "warning");
    const runJobResponse = await runJob(selectedJob?.header?.id!, selectedJob?.header?.name!);
    if(runJobResponse?.header){
      const combinedHeaders = [runJobResponse.header , ...jobRunHeaders];
      const sortedJobRunHeader = sortBy(combinedHeaders, (header) => header.created_date_ms!, "desc");
      setJobRunHeaders([...sortedJobRunHeader]);
    }
    runModal.onClose();
  };

  return (
    <Box display="flex" flexDirection="column" height="100%" mb={0}>
      {/* Fixed Heading */}
      <Box p={1} height="auto">
        <HStack justify="space-between">
          <HStack>
            <Icon as={FaRunning} boxSize="4" color="black" />
            <Text
              fontWeight="bold"
              align="left"
              m={1}
              ml={0}
              color="gray.700"
              fontSize="sm"
            >
              Jobs
            </Text>
          </HStack>

          <VStack
            align="center"
            justify="center"
            cursor="pointer"
            borderRadius="md"
          >
            <Button
              aria-label="Add new item"
              leftIcon={<AddIcon boxSize={3} />}
              colorScheme="purple.700"
              size="xs"
              variant="outline"
              onClick={() => {
                createModal.onOpen();
                setNewJobName("");
              }}
            >
              New
            </Button>
          </VStack>
        </HStack>
        <Divider my={2} mb={0} borderColor="purple.700" />
      </Box>

      {/* Scrollable Content */}
      <Stack
        height="100%"
        spacing="1"
        alignItems="left"
        overflowY="auto"
        sx={{
          "::-webkit-scrollbar": {
            width: "4px",
          },
          "::-webkit-scrollbar-track": {
            background: "gray.100",
          },
          "::-webkit-scrollbar-thumb": {
            background: "gray.500",
            borderRadius: "8px",
          },
          "::-webkit-scrollbar-thumb:hover": {
            background: "gray.600",
          },
          scrollbarWidth: "thin",
          scrollbarColor: "gray.500 gray.100",
        }}
      >
        {jobLoaders.headerLoader ? (
          <Center height="20%">
            <Spinner size="md" color="purple.700" />
          </Center>
        ) : (
          <List>
            {jobHeaders.map((header) => (
              <Box key={header.id}>
                <ListItem
                  role="group"
                  cursor="pointer"
                  onClick={() => handleJobClick(header)}
                  bg={selectedJobId === header.id ? "purple.700" : "transparent"}
                  color={selectedJobId === header.id ? "white" : "black"}
                  borderRadius="md"
                  p={1}
                  pl={2}
                  justifyContent="space-between"
                  display="flex"
                  alignItems="center"
                  fontSize="xs"
                  fontWeight={selectedJobId === header.id ? 700 : 600}
                  _hover={{
                    bg: selectedJobId === header.id ? "purple.700" : "purple.50",
                  }}
                  textAlign="left"
                >
                  {header.name}
                  <Box display="flex" alignItems="center">
                    <HStack spacing="0">
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          aria-label="Options"
                          icon={<BsThreeDotsVertical />}
                          variant="ghost"
                          size="xs"
                          color={selectedJobId === header.id ? "white" : "black"}
                          onClick={(e) => handleMenuClick(header, e)}
                          opacity={selectedJobId === header.id ? "1" : "0"}
                          _groupHover={{ opacity: "1" }}
                        />
                        <MenuList>
                          <MenuItem
                            fontSize="xs"
                            fontWeight={600}
                            color="black"
                            icon={<Icon as={FaPlay} boxSize={4} />}
                            onClick={() => handleActionClick(header, "run")}
                          >
                            Run Job
                          </MenuItem>
                          <MenuItem
                            fontSize="xs"
                            fontWeight={600}
                            color="black"
                            icon={<Icon as={MdEdit} boxSize={4} />}
                            onClick={() => handleActionClick(header, "rename")}
                          >
                            Rename
                          </MenuItem>
                          <MenuItem
                            fontSize="xs"
                            fontWeight={600}
                            color="black"
                            icon={<Icon as={IoDuplicate} boxSize={4} />}
                            onClick={() => handleActionClick(header, "duplicate")}
                          >
                            Duplicate
                          </MenuItem>
                          <MenuItem
                            fontSize="xs"
                            fontWeight={600}
                            color="red"
                            icon={<Icon as={FaTrash} boxSize={4} />}
                            onClick={() => handleActionClick(header, "delete")}
                          >
                            Delete Job
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </HStack>
                  </Box>
                </ListItem>
              </Box>
            ))}
          </List>
        )}
      </Stack>

      {/* Modals */}
      <DeleteJobModal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.onClose}
        selectedJob={selectedJob}
        handleDeleteJob={handleDeleteJob}
      />
      <RunJobModal
        isOpen={runModal.isOpen}
        onClose={runModal.onClose}
        reports={jobHeaders}
        selectedReport={selectedReport}
        handleReportHeaderSelect={handleReportHeaderSelect}
        reportError={null}
        dateError={null}
        updateContextInJobSteps={updateJobRunHeaders}
        dateFilter={dateFilter}
        dateFilterCategory={dateFilterCategory}
        setDateFilter={setDateFilter}
        setDateFilterCategory={setDateFilterCategory}
      />
      <CreateJobModal
        isOpen={createModal.isOpen}
        onClose={createModal.onClose}
        newJobName={newJobName}
        setNewJobName={setNewJobName}
        handleCreateJob={handleCreateJob}
      />
      <RenameJobModal
        isOpen={renameModal.isOpen}
        onClose={renameModal.onClose}
        newJobName={newJobName}
        setNewJobName={setNewJobName}
        handleRenameJob={handleRenameJob}
      />
    </Box>
  );
};

export default JobsContainer;
